/**
 * Owner-side reviews fetcher.
 *
 * Polls every 30s while the panel is mounted (real-time push lands in Phase 5).
 * Exposes mutate helpers that optimistically update local state and patch the server.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listReviewsForConversation,
  patchReviewComment,
  deleteReviewById,
} from '../../../services/reviewsAPI';
import { conversationUpdatesService } from '../../../services/conversationUpdatesService';
import type { OwnerReview, OwnerReviewComment, ReviewCommentStatus } from './types';

const POLL_INTERVAL_MS = 30_000;

interface UseReviewsResult {
  reviews: OwnerReview[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateCommentStatus: (
    reviewId: string,
    commentId: string,
    update: { status?: ReviewCommentStatus; creatorNote?: string | null; sentMessageId?: string | null },
  ) => Promise<OwnerReviewComment | null>;
  removeReview: (reviewId: string) => Promise<void>;
}

export function useReviews(conversationId: string | null, enabled: boolean): UseReviewsResult {
  const [reviews, setReviews] = useState<OwnerReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!conversationId || !enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      const res = await listReviewsForConversation(conversationId);
      setReviews(res.reviews);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load reviews');
    } finally {
      inFlightRef.current = false;
    }
  }, [conversationId, enabled]);

  // Initial fetch + polling
  useEffect(() => {
    if (!conversationId || !enabled) return;
    setLoading(true);
    fetchOnce().finally(() => setLoading(false));
    const id = window.setInterval(() => {
      fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [conversationId, enabled, fetchOnce]);

  // Real-time refresh when a new review arrives (Redis → SSE → client)
  useEffect(() => {
    if (!conversationId || !enabled) return;
    const unsubscribe = conversationUpdatesService.onConversationUpdate((cid, updates) => {
      if (cid !== conversationId) return;
      if (updates?.updateType === 'review.created') {
        fetchOnce();
      }
    });
    return unsubscribe;
  }, [conversationId, enabled, fetchOnce]);

  const updateCommentStatus = useCallback<UseReviewsResult['updateCommentStatus']>(
    async (reviewId, commentId, update) => {
      if (!conversationId) return null;
      // Optimistic update
      setReviews((prev) =>
        prev.map((r) =>
          r.id !== reviewId
            ? r
            : {
                ...r,
                comments: r.comments.map((c) =>
                  c.id !== commentId
                    ? c
                    : {
                        ...c,
                        status: update.status ?? c.status,
                        creatorNote: update.creatorNote !== undefined ? update.creatorNote : c.creatorNote,
                        sentMessageId: update.sentMessageId !== undefined ? update.sentMessageId : c.sentMessageId,
                        sentAt: update.status === 'sent' ? new Date().toISOString() : update.status ? null : c.sentAt,
                      },
                ),
              },
        ),
      );
      try {
        const res = await patchReviewComment(conversationId, reviewId, commentId, update);
        return res.comment;
      } catch (err: any) {
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to update comment');
        // Roll back by re-fetching
        await fetchOnce();
        return null;
      }
    },
    [conversationId, fetchOnce],
  );

  const removeReview = useCallback<UseReviewsResult['removeReview']>(
    async (reviewId) => {
      if (!conversationId) return;
      const snapshot = reviews;
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      try {
        await deleteReviewById(conversationId, reviewId);
      } catch (err: any) {
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to delete review');
        setReviews(snapshot);
      }
    },
    [conversationId, reviews],
  );

  return { reviews, loading, error, refresh: fetchOnce, updateCommentStatus, removeReview };
}
